import { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useSnackbar } from '../contexts/SnackbarContext';

export default function Suppliers() {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ name: '', contact: '' });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const res = await API.get('/suppliers');
      setSuppliers(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement des fournisseurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/suppliers', form);
      setForm({ name: '', contact: '' });
      showSnackbar('Fournisseur cree', 'success');
      fetchSuppliers();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors de la creation', 'error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          Fournisseurs
        </Typography>

        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 500 }}>
          Gestion des fournisseurs et contacts.
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={4}
          justifyContent="center"
        >
          <Chip
            label={`Fournisseurs: ${suppliers.length}`}
            sx={{ bgcolor: '#6366f1', color: 'var(--text-primary)' }}
          />
        </Stack>
      </Box>

      <Paper
        sx={{
          p: 4,
          mb: 6,
          borderRadius: 4,
          background:
            'var(--surface-2)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowForm((v) => !v)}
            disabled={user?.role !== 'ADMIN'}
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              px: 3,
              borderRadius: 3,
              fontWeight: 600,
              background: 'linear-gradient(90deg, #22c55e, #0ea5e9)',
            }}
          >
            {showForm ? 'Fermer' : 'Ajouter'}
          </Button>
        </Stack>

        {showForm && (
          <Box component="form" onSubmit={handleSubmit}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                name="name"
                label="Nom"
                value={form.name}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                name="contact"
                label="Contact"
                value={form.contact}
                onChange={handleChange}
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  px: 4,
                  borderRadius: 3,
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #22c55e, #0ea5e9)',
                }}
              >
                Enregistrer
              </Button>
            </Stack>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ color: 'var(--text-primary)' }} />
            </Stack>
          ) : isMobile ? (
            <Stack spacing={2}>
              {suppliers.map((s) => (
                <Paper
                  key={s.id}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    background:
                      'var(--surface-1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <Stack spacing={0.5}>
                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                      {s.name}
                    </Typography>
                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      Contact: {s.contact || '-'}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Paper
              sx={{
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-subtle)',
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ maxHeight: { xs: 420, md: 560 } }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['ID', 'Nom', 'Contact'].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                            borderBottom: '1px solid var(--border-subtle)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suppliers.map((s) => (
                      <TableRow
                        key={s.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'var(--surface-3)',
                          },
                        }}
                      >
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {s.id}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {s.name}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {s.contact || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
}




